import React, { useState, useRef, useEffect } from 'react';
import './Contact.css';
import emailjs from '@emailjs/browser';
import { 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt, 
  FaWhatsapp, 
  FaClock,
  FaPaperPlane,
  FaCheckCircle,
  FaExclamationCircle
} from 'react-icons/fa';

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: false,
    message: ''
  });
  const [errors, setErrors] = useState({});
  
  const sectionRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Le téléphone est requis';
    } else if (!/^[0-9\s+()-]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Numéro de téléphone invalide';
    }

    if (!formData.service) {
      newErrors.service = 'Veuillez sélectionner un service';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Le message doit contenir au moins 10 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setFormStatus({
      submitting: true,
      submitted: false,
      error: false,
      message: ''
    });

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID || 'your_service_id',
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'your_template_id',
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          service: formData.service,
          message: formData.message,
          to_name: 'PlumbThe2nd'
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'your_public_key'
      );

      setFormStatus({
        submitting: false,
        submitted: true,
        error: false,
        message: 'Votre message a été envoyé avec succès ! Nous vous recontacterons dans les plus brefs délais.'
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });

      // Hide success message after 5 seconds
      setTimeout(() => {
        setFormStatus(prev => ({ ...prev, submitted: false }));
      }, 5000);

    } catch (error) {
      console.error('EmailJS Error:', error);
      setFormStatus({
        submitting: false,
        submitted: false,
        error: true,
        message: 'Une erreur est survenue. Veuillez réessayer ou nous contacter directement par téléphone.'
      });

      // Hide error message after 5 seconds
      setTimeout(() => {
        setFormStatus(prev => ({ ...prev, error: false }));
      }, 5000);
    }
  };

  const services = [
    'Dépannage d\'urgence',
    'Détection de fuites',
    'Installation sanitaire',
    'WC & Évacuations',
    'Chauffe-eau',
    'Rénovation complète',
    'Autre'
  ];

  return (
    <section id="contact" className="section contact" ref={sectionRef}>
      <div className="contact-bg">
        <div className="contact-shape shape-1"></div>
        <div className="contact-shape shape-2"></div>
      </div>

      <div className="container">
        <div className="section-title">
          <h2>Contactez-nous</h2>
          <p>Demandez votre devis gratuit ou contactez-nous pour toute question</p>
        </div>

        <div className="contact-content">
          {/* Contact Info */}
          <div className={`contact-info ${isVisible ? 'reveal active' : 'reveal'}`}>
            <div className="info-header">
              <h3>Informations de Contact</h3>
              <p>N'hésitez pas à nous contacter par le moyen qui vous convient le mieux</p>
            </div>

            <div className="info-cards">
              <div className="info-card">
                <div className="info-icon phone-icon">
                  <FaPhone />
                </div>
                <div className="info-content">
                  <h4>Téléphone</h4>
                  <a href={`tel:+${process.env.REACT_APP_WHATSAPP_NUMBER || '33123456789'}`}>
                    {process.env.REACT_APP_PHONE || '01 23 45 67 89'}
                  </a>
                  <span className="info-note">Disponible 7j/7</span>
                </div>
              </div>

              <div className="info-card">
                <div className="info-icon email-icon">
                  <FaEnvelope />
                </div>
                <div className="info-content">
                  <h4>Email</h4>
                  <a href={`mailto:${process.env.REACT_APP_EMAIL || 'contact@plumbthe2nd.fr'}`}>
                    {process.env.REACT_APP_EMAIL || 'contact@plumbthe2nd.fr'}
                  </a>
                  <span className="info-note">Réponse sous 24h</span>
                </div>
              </div>

              <div className="info-card">
                <div className="info-icon whatsapp-icon">
                  <FaWhatsapp />
                </div>
                <div className="info-content">
                  <h4>WhatsApp</h4>
                  <a 
                    href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '33123456789'}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Message instantané
                  </a>
                  <span className="info-note">Réponse rapide</span>
                </div>
              </div>

              <div className="info-card">
                <div className="info-icon location-icon">
                  <FaMapMarkerAlt />
                </div>
                <div className="info-content">
                  <h4>Zone d'intervention</h4>
                  <p>Île-de-France</p>
                  <span className="info-note">Déplacement gratuit</span>
                </div>
              </div>
            </div>

            <div className="opening-hours">
              <div className="hours-icon">
                <FaClock />
              </div>
              <div className="hours-content">
                <h4>Horaires d'ouverture</h4>
                <div className="hours-list">
                  <div className="hours-item">
                    <span>Lundi - Vendredi</span>
                    <span>8h00 - 19h00</span>
                  </div>
                  <div className="hours-item">
                    <span>Samedi</span>
                    <span>9h00 - 18h00</span>
                  </div>
                  <div className="hours-item">
                    <span>Dimanche</span>
                    <span>Urgences uniquement</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className={`contact-form-wrapper ${isVisible ? 'reveal active' : 'reveal'}`}>
            <div className="form-header">
              <h3>Demande de Devis Gratuit</h3>
              <p>Remplissez le formulaire et nous vous recontacterons rapidement</p>
            </div>

            <form ref={formRef} onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Nom complet *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? 'error' : ''}
                    placeholder="Votre nom"
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Téléphone *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={errors.phone ? 'error' : ''}
                    placeholder="06 12 34 56 78"
                  />
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'error' : ''}
                    placeholder="votre@email.com"
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="service">Service souhaité *</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className={errors.service ? 'error' : ''}
                  >
                    <option value="">Sélectionnez un service</option>
                    {services.map((service, index) => (
                      <option key={index} value={service}>{service}</option>
                    ))}
                  </select>
                  {errors.service && <span className="error-message">{errors.service}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Décrivez votre projet *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className={errors.message ? 'error' : ''}
                  placeholder="Décrivez-nous votre besoin en détail..."
                  rows="6"
                ></textarea>
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>

              <button 
                type="submit" 
                className="btn btn-primary btn-large submit-btn"
                disabled={formStatus.submitting}
              >
                {formStatus.submitting ? (
                  <>
                    <div className="spinner"></div>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <FaPaperPlane /> Envoyer ma demande
                  </>
                )}
              </button>

              <p className="form-note">
                * Champs obligatoires. Vos données sont sécurisées et ne seront jamais partagées.
              </p>
            </form>

            {/* Success Message */}
            {formStatus.submitted && (
              <div className="form-message success-message">
                <FaCheckCircle className="message-icon" />
                <p>{formStatus.message}</p>
              </div>
            )}

            {/* Error Message */}
            {formStatus.error && (
              <div className="form-message error-message-box">
                <FaExclamationCircle className="message-icon" />
                <p>{formStatus.message}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
